import { resolveCountryName } from "./countries";
import { resolveMediaUrl } from "./userIdentity";

export const PHOTO_TARGET = 16;

const PHOTO_IMAGE_KEYS = [
  "interior_images_",
  "exterior_images_",
  "wheels_images_",
  "treads_images_",
];

/** Count uploaded photos only (ignore empty API placeholder slots). */
export function countEvaluationPhotos(record = {}) {
  return PHOTO_IMAGE_KEYS.reduce((sum, key) => {
    const list = record[key];
    if (!Array.isArray(list)) return sum;
    return (
      sum +
      list.filter((item) => Boolean(resolveMediaUrl(item?.image || item?.url || item)))
        .length
    );
  }, 0);
}

export function questionHasChoices(question) {
  return Array.isArray(question?.choices) && question.choices.length > 0;
}

export function getAnswerableQuestions(questions = []) {
  if (!Array.isArray(questions)) {
    return [];
  }
  return questions.filter(questionHasChoices);
}

export function countAnswerableQuestions(questions = []) {
  return getAnswerableQuestions(questions).length;
}

export function countAnsweredVehicleQuestions(vehicleDetails) {
  if (!Array.isArray(vehicleDetails) || vehicleDetails.length === 0) {
    return 0;
  }
  return new Set(vehicleDetails.map((detail) => String(detail.question))).size;
}

export function countAnsweredAnswerableQuestions(vehicleDetails, questions = []) {
  const answerableIds = new Set(
    getAnswerableQuestions(questions).map((q) => String(q.question_id))
  );

  if (answerableIds.size === 0) {
    return countAnsweredVehicleQuestions(vehicleDetails);
  }

  if (!Array.isArray(vehicleDetails) || vehicleDetails.length === 0) {
    return 0;
  }

  return vehicleDetails.filter((detail) =>
    answerableIds.has(String(detail.question))
  ).length;
}

export function areAnswerableQuestionsComplete(vehicleDetails, questions = []) {
  const required = countAnswerableQuestions(questions);
  if (required === 0) {
    return true;
  }
  return countAnsweredAnswerableQuestions(vehicleDetails, questions) >= required;
}

export function hasVehicleAddress(vehicleAddress) {
  return Array.isArray(vehicleAddress) && vehicleAddress.length > 0;
}

export function isVehicleProfileComplete(
  vehicleDetails,
  questions = [],
  vehicleAddress
) {
  return (
    areAnswerableQuestionsComplete(vehicleDetails, questions) &&
    hasVehicleAddress(vehicleAddress)
  );
}

export function getVehicleStepMetrics(
  vehicleDetails,
  questions = [],
  vehicleAddress,
  totalQuestionFallback = 0
) {
  const hasQuestionsLoaded = Array.isArray(questions) && questions.length > 0;
  const fallbackCount = Number(totalQuestionFallback) || 0;
  const answerableCount = hasQuestionsLoaded
    ? countAnswerableQuestions(questions)
    : fallbackCount;

  const answeredCount = hasQuestionsLoaded
    ? countAnsweredAnswerableQuestions(vehicleDetails, questions)
    : countAnsweredVehicleQuestions(vehicleDetails);

  const hasAddress = hasVehicleAddress(vehicleAddress);

  // Until showfeature questions load, never treat address-only as complete —
  // that unlocked photos/booking and could show a false "all steps done".
  const complete = hasQuestionsLoaded
    ? isVehicleProfileComplete(vehicleDetails, questions, vehicleAddress)
    : answerableCount > 0
      ? answeredCount >= answerableCount && hasAddress
      : false;

  let progress = 0;
  if (complete) {
    progress = 100;
  } else if (!hasQuestionsLoaded && answerableCount === 0) {
    progress = hasAddress ? 50 : answeredCount > 0 ? 25 : 0;
  } else if (answerableCount === 0) {
    progress = hasAddress ? 100 : 0;
  } else {
    const done = answeredCount + (hasAddress ? 1 : 0);
    const total = answerableCount + 1;
    progress = Math.min(100, Math.round((done / total) * 100));
    if (progress >= 100 && !complete) {
      progress = 99;
    }
  }

  return {
    complete,
    progress,
    answerableCount,
    answeredCount,
    hasAddress,
    questionsLoaded: hasQuestionsLoaded,
  };
}

export function getResumeWizardIndex(
  questions = [],
  vehicleDetails = [],
  vehicleAddress
) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return 0;
  }

  const answeredIds = new Set(
    (vehicleDetails || []).map((detail) => String(detail.question))
  );

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    if (!questionHasChoices(question)) {
      continue;
    }
    if (!answeredIds.has(String(question.question_id))) {
      return i;
    }
  }

  if (hasVehicleAddress(vehicleAddress)) {
    return questions.length;
  }

  return questions.length;
}

export function getNextWizardIndex(currentIndex, questions = []) {
  let nextIndex = currentIndex + 1;

  while (nextIndex < questions.length && !questionHasChoices(questions[nextIndex])) {
    nextIndex += 1;
  }

  return nextIndex;
}

export function buildDivindexFromVehicleDetails(vehicleDetails, questions = []) {
  const divindex = {};

  if (!Array.isArray(vehicleDetails) || vehicleDetails.length === 0) {
    return divindex;
  }

  vehicleDetails.forEach((detail, i) => {
    const featureIds = (detail.feature || []).map((item) => item.id);
    const questionIndex = questions.findIndex(
      (q) => String(q.question_id) === String(detail.question)
    );
    const targetIndex = questionIndex >= 0 ? questionIndex : i;
    divindex[targetIndex] = featureIds;
  });

  return divindex;
}

export function isChoiceSelected(divindex, questionIndex, choiceId) {
  const selected = divindex?.[questionIndex];
  if (!Array.isArray(selected)) return false;
  return selected.some((id) => String(id) === String(choiceId));
}

function displayAddressValue(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "-" ? "" : text;
}

export function mapSavedAddressToForm(savedAddress) {
  if (!savedAddress || typeof savedAddress !== "object") {
    return null;
  }

  return {
    line1: displayAddressValue(savedAddress.line1),
    line2: displayAddressValue(savedAddress.line2),
    line3:
      displayAddressValue(savedAddress.line3) ||
      displayAddressValue(savedAddress.city),
    address: displayAddressValue(savedAddress.address),
    country: resolveCountryName(displayAddressValue(savedAddress.country)),
    postCode: displayAddressValue(savedAddress.post_code || savedAddress.postcode),
  };
}
